import { useEffect, useRef } from 'react';
import type { SerializedKitAsset } from '../../../../../redux/api/fymAPI.types';
import { useQueryParam } from '../../../../../utils/hooks/useQueryParam';

interface UseEmailLinkActionsProps {
  assets: SerializedKitAsset[];
  /** "Download all": asks for the zip of the kit. */
  downloadAll: () => void;
  /** "Post it": opens the Composer with the file of the asset. */
  postAsset: (asset: SerializedKitAsset) => void;
}

/**
 * The two actions that the work emails start on the song page (music24-api
 * `kit/workEmail/workEmailLinks.ts`): `?zip=1` asks for the zip, `?post=<asset uuid>` opens the
 * Composer with that ready asset. Each runs once per page visit, when the data is there.
 */
export function useEmailLinkActions({ assets, downloadAll, postAsset }: UseEmailLinkActionsProps): void {
  const zip = useQueryParam('zip', undefined, '');
  const post = useQueryParam('post', undefined, '');
  const zipDone = useRef(false);
  const postDone = useRef(false);

  useEffect(() => {
    if (zip !== '1' || zipDone.current || !assets.some((asset) => asset.status === 'ready')) {
      return;
    }
    zipDone.current = true;
    downloadAll();
  }, [assets, downloadAll, zip]);

  useEffect(() => {
    if (post === '' || postDone.current) {
      return;
    }
    const asset = assets.find((one) => one.uuid === post && one.status === 'ready');
    if (asset === undefined) {
      return;
    }
    postDone.current = true;
    postAsset(asset);
  }, [assets, post, postAsset]);
}
